import { useEffect, useState, useRef, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { useAuth } from "@/shared/lib/auth";
import FileUpload from "@/shared/components/FileUpload";
import {
  Map, MapPin, Upload, X, Trash2, Plus, CheckCircle2, Circle, Move, Pencil, Minimize2, Maximize2,
  FileText, ImageIcon, Download, ChevronDown, User, ShieldCheck, Ruler,
} from "lucide-react";
import { toast } from "sonner";

const fileSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

// pin colour by item kind
const KIND_PIN = {
  wall: { color: "text-orange-600", fill: "fill-orange-300", dot: "bg-orange-500", label: "Wall" },
  glass: { color: "text-sky-600", fill: "fill-sky-300", dot: "bg-sky-500", label: "Glass" },
  others: { color: "text-violet-600", fill: "fill-violet-300", dot: "bg-violet-500", label: "Other" },
  signage: { color: "text-emerald-600", fill: "fill-emerald-300", dot: "bg-emerald-500", label: "Signage" },
  interactive: { color: "text-rose-600", fill: "fill-rose-300", dot: "bg-rose-500", label: "Interactive Wall" },
};
const pinKind = (k) => KIND_PIN[k] || KIND_PIN.wall;

// custom colour groups for "Others" pins — lets similar items share a colour
const OTHER_COLORS = [
  { v: "violet",  label: "Violet",  color: "text-violet-600",  fill: "fill-violet-300",  dot: "bg-violet-500" },
  { v: "fuchsia", label: "Fuchsia", color: "text-fuchsia-600", fill: "fill-fuchsia-300", dot: "bg-fuchsia-500" },
  { v: "teal",    label: "Teal",    color: "text-teal-600",    fill: "fill-teal-300",    dot: "bg-teal-500" },
  { v: "lime",    label: "Lime",    color: "text-lime-600",    fill: "fill-lime-300",    dot: "bg-lime-500" },
  { v: "indigo",  label: "Indigo",  color: "text-indigo-600",  fill: "fill-indigo-300",  dot: "bg-indigo-500" },
  { v: "cyan",    label: "Cyan",    color: "text-cyan-600",    fill: "fill-cyan-300",    dot: "bg-cyan-500" },
  { v: "yellow",  label: "Yellow",  color: "text-yellow-500",  fill: "fill-yellow-300",  dot: "bg-yellow-400" },
  { v: "stone",   label: "Stone",   color: "text-stone-600",   fill: "fill-stone-400",   dot: "bg-stone-500" },
  // 15 extras — deliberately distinct from the kind colours (orange/sky/emerald/rose)
  { v: "red",     label: "Red",     color: "text-red-600",     fill: "fill-red-300",     dot: "bg-red-500" },
  { v: "crimson", label: "Crimson", color: "text-red-800",     fill: "fill-red-500",     dot: "bg-red-700" },
  { v: "amber",   label: "Amber",   color: "text-amber-600",   fill: "fill-amber-300",   dot: "bg-amber-500" },
  { v: "brown",   label: "Brown",   color: "text-amber-800",   fill: "fill-amber-600",   dot: "bg-amber-700" },
  { v: "olive",   label: "Olive",   color: "text-yellow-700",  fill: "fill-yellow-600",  dot: "bg-yellow-600" },
  { v: "green",   label: "Green",   color: "text-green-600",   fill: "fill-green-300",   dot: "bg-green-500" },
  { v: "forest",  label: "Forest",  color: "text-green-800",   fill: "fill-green-600",   dot: "bg-green-700" },
  { v: "blue",    label: "Blue",    color: "text-blue-600",    fill: "fill-blue-300",    dot: "bg-blue-500" },
  { v: "navy",    label: "Navy",    color: "text-blue-900",    fill: "fill-blue-600",    dot: "bg-blue-800" },
  { v: "purple",  label: "Purple",  color: "text-purple-600",  fill: "fill-purple-300",  dot: "bg-purple-500" },
  { v: "plum",    label: "Plum",    color: "text-purple-900",  fill: "fill-purple-600",  dot: "bg-purple-800" },
  { v: "pink",    label: "Pink",    color: "text-pink-600",    fill: "fill-pink-300",    dot: "bg-pink-500" },
  { v: "slate",   label: "Slate",   color: "text-slate-600",   fill: "fill-slate-400",   dot: "bg-slate-500" },
  { v: "gray",    label: "Gray",    color: "text-gray-500",    fill: "fill-gray-300",    dot: "bg-gray-400" },
  { v: "black",   label: "Black",   color: "text-zinc-900",    fill: "fill-zinc-600",    dot: "bg-zinc-800" },
];
const otherColor = (v) => OTHER_COLORS.find((c) => c.v === v);
// effective style for a pin: "others" pins may carry a custom colour group
const pinStyle = (pin) =>
  (pin.kind === "others" && otherColor(pin.color)) || pinKind(pin.kind);

export default function FloorplanPortal() {
  const { user } = useAuth();
  const canCreate = ["admin", "management", "csm"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [projects, setProjects] = useState([]);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [addFloorOpen, setAddFloorOpen] = useState(false);
  const [floorId, setFloorId] = useState("");
  const [addMode, setAddMode] = useState(false);
  const [movePin, setMovePin] = useState(null);      // pin being repositioned
  const [pending, setPending] = useState(null);      // {x,y}
  const [active, setActive] = useState(null);        // selected pin
  const [wall, setWall] = useState(null);            // wall detail
  const [imgError, setImgError] = useState(false);   // floorplan image failed to load
  const imgWrap = useRef(null);

  useEffect(() => {
    api.get("/floorplan/projects").then((r) => {
      const list = r.data || [];
      setProjects(list);
      if (list.length && !code) setCode(list[0].project_code);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const load = useCallback(() => {
    if (!code) return;
    api.get(`/projects/${code}/floorplan`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [code]);
  useEffect(() => { load(); setActive(null); setWall(null); setPending(null); setAddMode(false); setMovePin(null); setPinTarget(null); setImgError(false); setFloorId(""); setAddFloorOpen(false); }, [code, load]);

  // multi-floor: floors list + the active floor tab
  const floors = data?.floors || [];
  const activeFloor = floors.find((f) => f.id === floorId) || floors[0] || null;
  const activeFloorId = activeFloor?.id || "";
  const switchFloor = (fid) => { setFloorId(fid); setActive(null); setWall(null); setPending(null); setMovePin(null); setImgError(false); };

  const [flashPinId, setFlashPinId] = useState(null);
  const [compact, setCompact] = useState(() => localStorage.getItem("fp_compact_pins") === "1");
  const [pinTarget, setPinTarget] = useState(null);  // {boq_id,label} pre-picked from the directory

  // category filter — which kinds (wall/glass/others/signage/interactive) are shown on the plan + directory
  const ALL_KIND_KEYS = ["wall", "glass", "others", "signage", "interactive"];
  const [kinds, setKinds] = useState(() => new Set(ALL_KIND_KEYS));
  const [othersColor, setOthersColor] = useState("");   // "" = all colours of Others
  const allKinds = kinds.size === ALL_KIND_KEYS.length;
  const toggleKind = (key) => setKinds((prev) => {
    if (prev.size === ALL_KIND_KEYS.length) return new Set([key]);   // from "all" → show only the clicked one
    const next = new Set(prev);
    if (next.has(key)) {
      next.delete(key);
      if (next.size === 0) return new Set(ALL_KIND_KEYS);            // never show nothing
    } else next.add(key);
    return next;
  });
  const selectAllKinds = () => { setKinds(new Set(ALL_KIND_KEYS)); setOthersColor(""); };
  const [othersMenu, setOthersMenu] = useState(false);

  const openPin = (pin) => {
    if (movePin) return;
    setActive(pin); setWall(null);
    api.get(`/projects/${code}/floorplan/wall/${pin.boq_id}`).then((r) => setWall(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
  };

  // reverse lookup: pick a wall from the directory → find & highlight its pin on the plan
  const locateWall = (boqId) => {
    const pin = (data?.pins || []).find((p) => p.boq_id === boqId);
    if (!pin) { toast.info("This wall isn't pinned on the plan yet."); return; }
    if (pin.floor_id && pin.floor_id !== activeFloorId) {
      const fl = floors.find((f) => f.id === pin.floor_id);
      switchFloor(pin.floor_id);
      if (fl) toast.info(`Switched to ${fl.name}.`);
    }
    openPin(pin);
    setFlashPinId(pin.id);
    setTimeout(() => {
      document.querySelector(`[data-testid="floorplan-pin-${pin.id}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }, 260);
    setTimeout(() => setFlashPinId((v) => (v === pin.id ? null : v)), 2600);
  };

  const coordsFrom = (e) => {
    const rect = imgWrap.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };
  const onImgClick = async (e) => {
    if (movePin) {
      const { x, y } = coordsFrom(e);
      try { await api.patch(`/projects/${code}/floorplan/pins/${movePin.id}`, { x, y }); toast.success("Pin moved."); setMovePin(null); load(); }
      catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
      return;
    }
    if (!addMode) return;
    const coords = coordsFrom(e);
    if (pinTarget) {
      // shortcut: a wall was pre-picked from the directory → drop its pin directly
      try {
        const scopeOf = (l) => (l || "").split("·").pop().trim();
        const same = pinTarget.kind === "others"
          ? (data?.pins || []).find((p) => p.kind === "others" && p.color && scopeOf(p.label) === scopeOf(pinTarget.label))
          : null;
        const res = await api.post(`/projects/${code}/floorplan/pins`, { ...coords, boq_id: pinTarget.boq_id, label: pinTarget.label, floor_id: activeFloorId, color: pinTarget.kind === "others" ? (same?.color || "violet") : "" });
        const placed = (pinTarget.placed || 0) + 1;
        const total = pinTarget.total || 1;
        if (total > 1 && res.data?.id) setBatchStack((s) => [...s, res.data.id]);
        if (placed < total) {
          // batch item (e.g. 11 directional signages): stay in placement mode
          setPinTarget({ ...pinTarget, placed });
          toast.success(`Pinned "${pinTarget.label}" — ${placed}/${total}. Click to place the next one.`);
        } else {
          toast.success(total > 1 ? `All ${total} pins placed for "${pinTarget.label}".` : `Pinned "${pinTarget.label}".`);
          setPinTarget(null); setAddMode(false);
        }
        load();
      } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
      return;
    }
    setPending(coords);
  };

  // shortcut from the directory: pre-pick a wall, then click the plan to place it
  // (batch items keep placement mode on until every piece is pinned)
  const startPinFor = (boq_id, label, total = 1, placed = 0, kind = "") => {
    setPinTarget({ boq_id, label, total, placed, kind });
    setBatchStack([]);
    setAddMode(true); setPending(null); setActive(null); setWall(null); setMovePin(null);
    const left = total - placed;
    toast.info(left > 1
      ? `Click the plan to place "${label}" — ${left} pins one after another (Esc to stop).`
      : `Click the plan to place "${label}".`);
  };

  // Esc always exits any placement / move mode
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setAddMode(false); setPinTarget(null); setPending(null); setMovePin(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // one-click undo of the last pin dropped in the current batch session
  const [batchStack, setBatchStack] = useState([]);
  const undoLastPin = async () => {
    const last = batchStack[batchStack.length - 1];
    if (!last) return;
    try {
      await api.delete(`/projects/${code}/floorplan/pins/${last}`);
      setBatchStack((s) => s.slice(0, -1));
      setPinTarget((t) => (t ? { ...t, placed: Math.max((t.placed || 0) - 1, 0) } : t));
      toast.success("Last pin removed — click the plan to place it again.");
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  async function uploadFloorplan(url, name, floorName) {
    try {
      const r = await api.post(`/projects/${code}/floorplans`, { file_url: url, file_name: name, floor_name: floorName || "" });
      toast.success(`"${r.data.name}" floorplan uploaded & locked.`);
      setAddFloorOpen(false);
      setFloorId(r.data.id);
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function replaceFloor(url, name, reason) {
    if (!activeFloor) return;
    try {
      await api.put(`/projects/${code}/floorplans/${activeFloor.id}`, { file_url: url, file_name: name, reason: reason || "" });
      toast.success("Floorplan replaced & locked.");
      setReplaceOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function deleteFloor(fl) {
    if (!window.confirm(`Delete "${fl.name}" and all its pins? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${code}/floorplans/${fl.id}`);
      toast.success(`"${fl.name}" removed.`);
      setFloorId("");
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  const [renaming, setRenaming] = useState(null);   // {id, name} while editing a tab
  async function renameFloor() {
    const name = (renaming?.name || "").trim();
    if (!name) { setRenaming(null); return; }
    try {
      await api.put(`/projects/${code}/floorplans/${renaming.id}`, { floor_name: name });
      toast.success("Floor renamed.");
      setRenaming(null);
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function deletePin(id) {
    try { await api.delete(`/projects/${code}/floorplan/pins/${id}`); toast.success("Pin removed."); setActive(null); setWall(null); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  const fp = activeFloor;
  const img = fp?.image_url ? fileSrc(fp.image_url) : "";
  const floorPins = (data?.pins || []).filter((p) => !activeFloorId || (p.floor_id || floors[0]?.id) === activeFloorId);

  return (
    <div className="min-h-full bg-zinc-100" data-testid="floorplan-page">
      {/* top bar */}
      <div className="bg-white border-b border-zinc-200 px-5 py-3 flex flex-wrap items-center gap-3 sticky top-0 z-20">
        <div className="flex items-center gap-2 mr-2">
          <div className="w-8 h-8 rounded-lg bg-sky-600 text-white flex items-center justify-center"><Map size={17} /></div>
          <span className="font-bold text-zinc-900">Floorplan</span>
        </div>
        <div className="relative">
          <select value={code} onChange={(e) => setCode(e.target.value)} data-testid="floorplan-project-select"
            className="appearance-none text-sm rounded-lg border border-zinc-300 pl-3 pr-8 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-sky-300 max-w-[280px]">
            {projects.map((p) => <option key={p.project_code} value={p.project_code}>{p.upr_no || p.project_code} · {p.name}</option>)}
          </select>
          <ChevronDown size={15} className="absolute right-2.5 top-2.5 text-zinc-400 pointer-events-none" />
        </div>
        <div className="flex-1" />
        {/* category filter — click to show only that kind; All shows everything */}
        <div className="flex items-center gap-1.5 mr-1" data-testid="floorplan-kind-filter">
          <button onClick={selectAllKinds} data-testid="floorplan-filter-all"
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors ${allKinds ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50"}`}>
            All
          </button>
          {KIND_ORDER.map(({ key, label }) => {
            const pk = pinKind(key);
            const on = !allKinds && kinds.has(key);
            if (key === "others") {
              const used = [...new Set(floorPins.filter((p) => p.kind === "others").map((p) => p.color || "violet"))];
              return (
                <div key={key} className="relative">
                  <button onClick={() => setOthersMenu((m) => !m)} data-testid="floorplan-filter-others"
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors ${on ? "border-zinc-900 bg-zinc-50 text-zinc-900" : "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50"}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${othersColor ? (otherColor(othersColor)?.dot || pk.dot) : pk.dot}`} />
                    {othersColor ? `Others · ${otherColor(othersColor)?.label || othersColor}` : label}
                    <ChevronDown size={11} className="text-zinc-400" />
                  </button>
                  {othersMenu && (
                    <div className="absolute right-0 top-8 z-30 w-44 rounded-xl border border-zinc-200 bg-white shadow-lg p-1.5" data-testid="floorplan-others-menu">
                      <button onClick={() => { setOthersColor(""); toggleKind("others"); setOthersMenu(false); }}
                        data-testid="floorplan-others-all"
                        className="w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50">
                        <span className={`w-2.5 h-2.5 rounded-full ${pk.dot}`} /> All “Others”
                      </button>
                      {used.length === 0 && (
                        <div className="px-2.5 py-1.5 text-[11px] text-zinc-400">No “Others” pins on this floor yet.</div>
                      )}
                      {used.map((v) => {
                        const c = otherColor(v) || OTHER_COLORS[0];
                        const cnt = floorPins.filter((p) => p.kind === "others" && (p.color || "violet") === v).length;
                        return (
                          <button key={v} onClick={() => { setOthersColor(v); setKinds(new Set(["others"])); setOthersMenu(false); }}
                            data-testid={`floorplan-others-color-${v}`}
                            className={`w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs hover:bg-zinc-50 ${othersColor === v ? "bg-zinc-100 font-semibold text-zinc-900" : "text-zinc-700"}`}>
                            <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} /> {c.label}
                            <span className="ml-auto text-[10px] text-zinc-400 tabular-nums">{cnt}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button key={key} onClick={() => { toggleKind(key); setOthersColor(""); }} data-testid={`floorplan-filter-${key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors ${on ? "border-zinc-900 bg-zinc-50 text-zinc-900" : "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50"}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${pk.dot}`} /> {label}
              </button>
            );
          })}
        </div>
        {img && (
          <button onClick={() => setCompact((c) => { localStorage.setItem("fp_compact_pins", c ? "0" : "1"); return !c; })}
            data-testid="floorplan-compact-toggle"
            title={compact ? "Show full pin markers" : "Shrink pins into small dots — clean view for dense floors"}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${compact ? "bg-zinc-900 text-white" : "border border-zinc-300 text-zinc-600 bg-white hover:bg-zinc-50"}`}>
            {compact ? <Maximize2 size={14} /> : <Minimize2 size={14} />} {compact ? "Full pins" : "Compact pins"}
          </button>
        )}
        {img && canCreate && (
          <button onClick={() => { setAddMode((m) => !m); setPending(null); setMovePin(null); setPinTarget(null); }} data-testid="floorplan-addpin-toggle"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${addMode ? "bg-sky-600 text-white" : "border border-sky-300 text-sky-700 bg-sky-50 hover:bg-sky-100"}`}>
            <MapPin size={15} /> {addMode ? (pinTarget ? `Click the plan: ${pinTarget.label}` : "Click the plan to drop a pin") : "Pin a wall"}
          </button>
        )}
        {fp?.source_url && (
          <a href={fileSrc(fp.source_url)} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 text-zinc-600 px-3 py-2 text-sm hover:bg-zinc-50" data-testid="floorplan-download-source">
            <Download size={14} /> Source
          </a>
        )}
        {fp && isAdmin && (
          <button onClick={() => setReplaceOpen(true)} data-testid="floorplan-replace-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 text-amber-700 bg-amber-50 px-3 py-2 text-sm font-medium hover:bg-amber-100">
            <Upload size={14} /> Replace
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-4 p-4">
        {/* floorplan canvas */}
        <div className="flex-1 min-w-0">
          {/* floor tabs — one plan per floor */}
          {floors.length > 0 && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="floorplan-floor-tabs">
              {floors.map((fl) => {
                const cnt = (data?.pins || []).filter((p) => (p.floor_id || floors[0]?.id) === fl.id).length;
                const on = fl.id === activeFloorId;
                if (renaming?.id === fl.id) {
                  return (
                    <span key={fl.id} className="inline-flex items-center gap-1 rounded-lg border border-sky-400 bg-white px-2 py-1">
                      <input autoFocus value={renaming.name}
                        onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") renameFloor(); if (e.key === "Escape") setRenaming(null); }}
                        data-testid="floorplan-floor-rename-input"
                        className="text-sm w-36 focus:outline-none" />
                      <button onClick={renameFloor} data-testid="floorplan-floor-rename-save" className="text-emerald-600 hover:text-emerald-800"><CheckCircle2 size={15} /></button>
                      <button onClick={() => setRenaming(null)} className="text-zinc-400 hover:text-zinc-600"><X size={14} /></button>
                    </span>
                  );
                }
                return (
                  <button key={fl.id} onClick={() => switchFloor(fl.id)} data-testid={`floorplan-floor-tab-${fl.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium border transition-colors ${on ? "bg-sky-600 text-white border-sky-600" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
                    {fl.name}
                    <span className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${on ? "bg-white/20" : "bg-zinc-100 text-zinc-500"}`}>{cnt}</span>
                    {on && isAdmin && (
                      <span role="button" onClick={(e) => { e.stopPropagation(); setRenaming({ id: fl.id, name: fl.name }); }}
                        data-testid={`floorplan-floor-rename-${fl.id}`} title="Rename floor"
                        className="ml-0.5 text-white/70 hover:text-white"><Pencil size={12} /></span>
                    )}
                    {on && isAdmin && floors.length > 1 && (
                      <span role="button" onClick={(e) => { e.stopPropagation(); deleteFloor(fl); }}
                        data-testid={`floorplan-floor-delete-${fl.id}`}
                        className="ml-0.5 text-white/70 hover:text-white"><Trash2 size={12} /></span>
                    )}
                  </button>
                );
              })}
              {canCreate && (
                <button onClick={() => setAddFloorOpen(true)} data-testid="floorplan-add-floor-btn"
                  className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium border border-dashed border-sky-300 text-sky-700 bg-sky-50/50 hover:bg-sky-50">
                  <Plus size={14} /> Add floor
                </button>
              )}
            </div>
          )}
          {!fp ? (
            canCreate ? <UploadZone onUploaded={uploadFloorplan} /> : (
              <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-12 text-center text-sm text-zinc-400">
                <Map className="mx-auto mb-3 text-zinc-300" size={28} />
                No floorplan uploaded for this project yet.
              </div>
            )
          ) : !img ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
              <FileText className="mx-auto mb-3 text-amber-500" size={28} />
              <p className="text-sm text-amber-800 font-medium">"{fp.source_name}" can't be previewed in the browser.</p>
              <p className="text-xs text-amber-600 mt-1 mb-4">{canCreate ? "Upload a PDF or image of the plan to pin walls on it." : "Ask the CSM to upload a PDF/image version to view it here."}</p>
              {canCreate && <div className="max-w-sm mx-auto"><UploadZone onUploaded={uploadFloorplan} compact /></div>}
            </div>
          ) : (
            <div className="rounded-2xl border border-zinc-200 bg-white overflow-auto shadow-sm">
              <div ref={imgWrap} onClick={onImgClick}
                className={`relative inline-block min-w-full ${(addMode || movePin) ? "cursor-crosshair" : ""}`}
                data-testid="floorplan-canvas">
                <img src={img} alt="Floorplan" className="block w-full select-none" draggable={false} />
                {floorPins.filter((pin) => kinds.has(pin.kind || "wall")
                    && (!othersColor || pin.kind !== "others" || (pin.color || "violet") === othersColor)).map((pin) => {
                  const pk = pinStyle(pin);
                  const dim = movePin && movePin.id !== pin.id;
                  const isActive = active?.id === pin.id;
                  return (
                    <button key={pin.id} onClick={(e) => { e.stopPropagation(); openPin(pin); }}
                      data-testid={`floorplan-pin-${pin.id}`}
                      style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                      className={`absolute -translate-x-1/2 ${compact ? "-translate-y-1/2" : "-translate-y-full"} group ${dim ? "opacity-30" : ""}`}>
                      {flashPinId === pin.id && (
                        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-zinc-900/15 animate-ping pointer-events-none" />
                      )}
                      {isActive && !compact && (
                        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[55%] w-7 h-7 rounded-full ring-2 ring-zinc-900 ring-offset-1 pointer-events-none" />
                      )}
                      {compact ? (
                        <span className={`block w-1.5 h-1.5 rounded-full ${pk.dot} ring-1 ring-white/80 shadow-sm transition-transform ${isActive ? "scale-[2.2] ring-2 ring-zinc-900" : "group-hover:scale-[1.8]"}`} />
                      ) : (
                        <MapPin size={isActive ? 20 : 17} className={`drop-shadow ${pk.color} ${pk.fill} ${isActive ? "scale-110" : ""}`} strokeWidth={isActive ? 2.6 : 2.2} />
                      )}
                      <span className={`absolute left-1/2 -translate-x-1/2 ${compact ? "top-2" : "top-4"} whitespace-nowrap text-[10px] font-medium bg-zinc-900 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10`}>{pin.label}</span>
                    </button>
                  );
                })}
                {pending && (
                  <div style={{ left: `${pending.x}%`, top: `${pending.y}%` }} className="absolute -translate-x-1/2 -translate-y-full">
                    <MapPin size={18} className="text-sky-600 fill-sky-300 animate-bounce" />
                  </div>
                )}
              </div>
            </div>
          )}
          {fp && img && !imgError && (
            <p className="text-[11px] text-zinc-400 mt-2 px-1" data-testid="floorplan-status-line">
              {floorPins.length} wall(s) pinned on {fp.name || "this floor"}. {movePin ? "Click the new spot to move the pin." : addMode ? (
                pinTarget && (pinTarget.total || 1) > 1 ? (
                  <span className="text-sky-600 font-medium">
                    Placing "{pinTarget.label}" — {pinTarget.placed || 0}/{pinTarget.total} placed. Click the plan for the next pin
                    {batchStack.length > 0 && (
                      <>
                        {" · "}
                        <button onClick={undoLastPin} data-testid="floorplan-batch-undo"
                                className="underline hover:text-sky-800">Undo last</button>
                      </>
                    )}
                    {" · "}
                    <button onClick={() => { setAddMode(false); setPinTarget(null); }} data-testid="floorplan-batch-done"
                            className="underline hover:text-sky-800">Done</button>
                  </span>
                ) : "Click anywhere on the plan to place a pin."
              ) : "Click a pin to see its design, pre-audit & progress."}
            </p>
          )}
        </div>

        {/* right detail / pin picker */}
        <div className="w-full lg:w-[360px] shrink-0">
          {pending ? (
            <PinPicker groups={data?.groups || []} pins={data?.pins || []} onCancel={() => setPending(null)}
              onSave={async (boq_id, label, color) => {
                try { await api.post(`/projects/${code}/floorplan/pins`, { ...pending, boq_id, label, floor_id: activeFloorId, color: color || "" }); toast.success("Wall pinned."); setPending(null); setAddMode(false); load(); }
                catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
              }} />
          ) : active ? (
            <WallDetail wall={wall} pin={active} canCreate={canCreate}
              moving={movePin?.id === active.id}
              onMove={() => { setMovePin(active); setActive(active); }}
              onRecolor={async (v) => {
                try {
                  await api.patch(`/projects/${code}/floorplan/pins/${active.id}`, { x: active.x, y: active.y, color: v });
                  setActive({ ...active, color: v });
                  toast.success("Pin colour updated.");
                  load();
                } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
              }}
              onClose={() => { setActive(null); setWall(null); setMovePin(null); }}
              onDelete={() => deletePin(active.id)} />
          ) : (
            <WallDirectory groups={data?.groups || []} pins={data?.pins || []}
              activeBoqId={active?.boq_id} canCreate={canCreate} kinds={kinds}
              onLocate={locateWall} onPin={startPinFor} />
          )}
        </div>
      </div>
      {replaceOpen && fp && (
        <ReplaceFloorplanModal fp={fp} onClose={() => setReplaceOpen(false)} onReplace={replaceFloor} />
      )}
      {addFloorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="floorplan-add-floor-modal" onClick={() => setAddFloorOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center"><Plus size={16} /></div>
                <h3 className="font-semibold text-zinc-900">Add a floor</h3>
              </div>
              <button onClick={() => setAddFloorOpen(false)} data-testid="floorplan-add-floor-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
            </div>
            <div className="px-5 py-4">
              <UploadZone compact defaultFloorName={`Floor ${floors.length + 1}`} onUploaded={uploadFloorplan} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const KIND_ORDER = [
  { key: "wall", label: "Walls" },
  { key: "glass", label: "Glass" },
  { key: "others", label: "Others" },
  { key: "signage", label: "Signage" },
  { key: "interactive", label: "Interactive" },
];

// Right-hand collapsible directory: sub-group → walls/glass/others → pick a wall
// to locate & highlight its pin on the plan (reverse of clicking a pin).
function WallDirectory({ groups, pins, activeBoqId, canCreate, kinds, onLocate, onPin }) {
  const pinnedIds = new Set((pins || []).map((p) => p.boq_id));
  const pinCountById = {};
  (pins || []).forEach((p) => { pinCountById[p.boq_id] = (pinCountById[p.boq_id] || 0) + 1; });
  const showKind = (k) => !kinds || kinds.has(k);
  const visibleGroups = (groups || [])
    .map((g) => ({ ...g, walls: (g.walls || []).filter((w) => showKind(w.kind || "wall")) }))
    .filter((g) => g.walls.length);
  const [open, setOpen] = useState(() => (groups[0] ? { [groups[0].id]: true } : {}));
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  if (!visibleGroups.length) {
    return (
      <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-8 text-center text-sm text-zinc-400" data-testid="floorplan-directory-empty">
        <MapPin className="mx-auto mb-2 text-zinc-300" size={24} />
        {groups.length ? "No items in the selected category." : "No sub-scope walls yet. Add walls in the Sub-scope workspace to see them here."}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="floorplan-directory">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <span className="w-7 h-7 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center"><Map size={15} /></span>
        <div>
          <div className="text-sm font-semibold text-zinc-900">Wall directory</div>
          <div className="text-[11px] text-zinc-400">Pick a wall to locate it on the plan</div>
        </div>
      </div>
      <div className="max-h-[70vh] overflow-auto divide-y divide-zinc-100">
        {visibleGroups.map((g) => {
          const isOpen = !!open[g.id];
          const pinnedCount = g.walls.filter((w) => pinnedIds.has(w.boq_id)).length;
          return (
            <div key={g.id} data-testid={`floorplan-dir-group-${g.id}`}>
              <button onClick={() => toggle(g.id)} data-testid={`floorplan-dir-group-toggle-${g.id}`}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-zinc-50 transition-colors">
                <span className="flex items-center gap-2 text-sm font-medium text-zinc-800">
                  <ChevronDown size={15} className={`text-zinc-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                  {g.name}
                </span>
                <span className="text-[11px] text-zinc-400">{pinnedCount}/{g.walls.length} on plan</span>
              </button>
              {isOpen && (
                <div className="pb-2">
                  {KIND_ORDER.map(({ key, label }) => {
                    if (!showKind(key)) return null;
                    const items = g.walls.filter((w) => (w.kind || "wall") === key);
                    if (!items.length) return null;
                    const pk = pinKind(key);
                    return (
                      <div key={key} className="px-4 pt-1.5">
                        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">
                          <span className={`w-2 h-2 rounded-full ${pk.dot}`} /> {label} · {items.length}
                        </div>
                        <div className="space-y-1">
                          {items.map((w) => {
                            const pinned = pinnedIds.has(w.boq_id);
                            const isActive = activeBoqId === w.boq_id;
                            const isBatch = (w.batch_count || 0) > 1;
                            const cnt = pinCountById[w.boq_id] || 0;
                            return (
                              <div key={w.boq_id}
                                className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border transition-colors ${isActive ? "border-sky-300 bg-sky-50" : pinned ? "border-zinc-200 hover:bg-zinc-50" : "border-dashed border-zinc-200 hover:bg-zinc-50"}`}>
                                <button onClick={() => (pinned ? onLocate(w.boq_id) : onPin?.(w.boq_id, w.label, w.batch_count || 1, cnt, w.kind))}
                                  data-testid={`floorplan-dir-wall-${w.boq_id}`}
                                  className="flex items-center gap-2 min-w-0 flex-1 text-left">
                                  <MapPin size={13} className={pinned ? pk.color : "text-zinc-300"} />
                                  <span className={`text-xs truncate ${pinned ? "text-zinc-700" : "text-zinc-400"}`}>
                                    {w.label}{isBatch ? <span className="text-zinc-400"> · {w.batch_count} pcs</span> : null}
                                  </span>
                                </button>
                                {isBatch ? (
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[10px] text-zinc-400 tabular-nums">{cnt}/{w.batch_count} pinned</span>
                                    {pinned && <button onClick={() => onLocate(w.boq_id)} className="text-[10px] text-emerald-600">Locate</button>}
                                    {canCreate && cnt < w.batch_count && (
                                      <button onClick={() => onPin?.(w.boq_id, w.label, w.batch_count || 1, cnt, w.kind)}
                                        data-testid={`floorplan-dir-addpin-${w.boq_id}`}
                                        className="inline-flex items-center gap-1 text-[10px] text-sky-600 hover:text-sky-800 font-medium">
                                        <Plus size={11} /> Add pin
                                      </button>
                                    )}
                                  </div>
                                ) : pinned ? (
                                  <span className="text-[10px] shrink-0 text-emerald-600">Locate</span>
                                ) : canCreate ? (
                                  <button onClick={() => onPin?.(w.boq_id, w.label, 1, 0, w.kind)}
                                    data-testid={`floorplan-dir-addpin-${w.boq_id}`}
                                    className="inline-flex items-center gap-1 text-[10px] shrink-0 text-sky-600 hover:text-sky-800 font-medium">
                                    <Plus size={11} /> Add pin
                                  </button>
                                ) : (
                                  <span className="text-[10px] shrink-0 text-zinc-400">Not pinned</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function UploadZone({ onUploaded, compact, defaultFloorName = "Floor 1" }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [floorName, setFloorName] = useState(defaultFloorName);
  return (
    <div className={`rounded-2xl border border-zinc-200 bg-white ${compact ? "p-4" : "p-10"}`} data-testid="floorplan-upload-zone">
      {!compact && (
        <div className="text-center mb-5">
          <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center mx-auto mb-3"><ImageIcon size={24} /></div>
          <h3 className="font-semibold text-zinc-900">Upload the project floorplan</h3>
          <p className="text-sm text-zinc-500 mt-1">PDF, DWG or image — PDFs are auto-converted so you can pin walls on them. Projects with multiple floors can add one plan per floor.</p>
        </div>
      )}
      <div className="max-w-md mx-auto space-y-3">
        <div>
          <label className="text-sm font-medium text-zinc-700">Floor name</label>
          <input value={floorName} onChange={(e) => setFloorName(e.target.value)} data-testid="floorplan-floor-name"
            placeholder="e.g. Ground Floor, 1st Floor, Mezzanine…"
            className="mt-1 w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-300" />
        </div>
        <FileUpload value={url} label="Floorplan file" onChange={(u) => { setUrl(u); setName(u ? (u.split("/").pop() || "floorplan") : ""); }} testid="floorplan-file-upload" />
        <button onClick={() => onUploaded(url, name, floorName.trim())} disabled={!url} data-testid="floorplan-upload-save"
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-sky-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-sky-700 disabled:opacity-50">
          <Upload size={15} /> Use this floorplan
        </button>
      </div>
    </div>
  );
}

function ReplaceFloorplanModal({ fp, onClose, onReplace }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const canSubmit = !!url && !!reason.trim();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="floorplan-replace-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><Upload size={16} /></div>
            <h3 className="font-semibold text-zinc-900">Replace floorplan{fp.name ? ` — ${fp.name}` : ""}</h3>
          </div>
          <button onClick={onClose} data-testid="floorplan-replace-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            The current floorplan is locked. Replacing it will keep an audit record. Current file: <span className="font-medium">{fp.source_name}</span>
          </p>
          <div>
            <label className="text-sm font-medium text-zinc-700">Reason for replacement <span className="text-rose-500">*</span></label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="floorplan-replace-reason"
              placeholder="e.g. The wrong floorplan was uploaded by mistake."
              className="mt-1 w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300" />
          </div>
          <FileUpload value={url} label="New floorplan file" onChange={(u) => { setUrl(u); setName(u ? (u.split("/").pop() || "floorplan") : ""); }} testid="floorplan-replace-upload" />
        </div>
        <div className="flex justify-end gap-2 border-t border-zinc-100 px-5 py-3">
          <button onClick={onClose} data-testid="floorplan-replace-cancel" className="rounded-lg border border-zinc-300 px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-50">Cancel</button>
          <button onClick={() => onReplace(url, name, reason.trim())} disabled={!canSubmit} data-testid="floorplan-replace-submit"
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
            <Upload size={15} /> Replace floorplan
          </button>
        </div>
      </div>
    </div>
  );
}


function PinPicker({ groups, pins = [], onCancel, onSave }) {
  const [gid, setGid] = useState("");
  const [wid, setWid] = useState("");
  const [color, setColor] = useState("violet");
  // an item can only be pinned once across all floors (batch items: once per piece)
  const pinCount = {};
  pins.forEach((p) => { pinCount[p.boq_id] = (pinCount[p.boq_id] || 0) + 1; });
  const pinnable = (w) => (pinCount[w.boq_id] || 0) < ((w.batch_count || 0) > 1 ? w.batch_count : 1);
  const avail = groups
    .map((g) => ({ ...g, walls: (g.walls || []).filter(pinnable) }))
    .filter((g) => g.walls.length);
  const group = avail.find((g) => g.id === gid);
  const wall = group?.walls.find((w) => w.boq_id === wid);
  const isOthers = wall?.kind === "others";
  // same scope (e.g. AMENITY ROOM SIGNAGE) → default to the colour its other pins already use
  const scopeOf = (lbl) => (lbl || "").split("·").pop().trim();
  const pickWall = (id) => {
    setWid(id);
    const w = group?.walls.find((x) => x.boq_id === id);
    if (w?.kind === "others") {
      const sc = scopeOf(w.label);
      const match = pins.find((p) => p.kind === "others" && p.color && scopeOf(p.label) === sc);
      setColor(match?.color || "violet");
    }
  };
  return (
    <div className="rounded-2xl border border-sky-200 bg-white p-5 shadow-sm" data-testid="floorplan-pin-picker">
      <div className="flex items-center justify-between mb-4">
        <span className="font-semibold text-zinc-900 flex items-center gap-2"><MapPin size={16} className="text-sky-600" /> Tag this pin</span>
        <button onClick={onCancel} data-testid="floorplan-pin-cancel" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
      </div>
      {!avail.length ? (
        <p className="text-xs text-zinc-500" data-testid="floorplan-pin-none-left">
          Every sub-scope item is already pinned. Remove a pin first if one belongs on a different floor.
        </p>
      ) : (<>
      <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">1 · Group</label>
      <select value={gid} onChange={(e) => { setGid(e.target.value); setWid(""); }} data-testid="floorplan-pick-group"
        className="w-full mt-1 mb-4 text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-300">
        <option value="">Select a group…</option>
        {avail.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.walls.length})</option>)}
      </select>
      {group && (
        <>
          <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">2 · Wall / glass</label>
          <select value={wid} onChange={(e) => pickWall(e.target.value)} data-testid="floorplan-pick-wall"
            className="w-full mt-1 mb-4 text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-300">
            <option value="">Select a wall…</option>
            {group.walls.map((w) => <option key={w.boq_id} value={w.boq_id}>{w.label}{w.designer_name ? ` · ${w.designer_name}` : ""}</option>)}
          </select>
        </>
      )}
      {isOthers && (
        <>
          <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">3 · Pin colour (group similar “Others” together)</label>
          <div className="flex items-center gap-2 mt-1.5 mb-4 flex-wrap" data-testid="floorplan-pick-color">
            {OTHER_COLORS.map((c) => (
              <button key={c.v} onClick={() => setColor(c.v)} title={c.label}
                data-testid={`floorplan-color-${c.v}`}
                className={`w-7 h-7 rounded-full ${c.dot} transition-transform ${color === c.v ? "ring-2 ring-offset-2 ring-zinc-900 scale-110" : "opacity-70 hover:opacity-100"}`} />
            ))}
          </div>
        </>
      )}
      <button onClick={() => onSave(wid, group ? `${group.name} · ${wall?.label}` : wall?.label, isOthers ? color : "")} disabled={!wid}
        data-testid="floorplan-pin-save"
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-sky-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-sky-700 disabled:opacity-50">
        <Plus size={15} /> Pin this wall
      </button>
      </>)}
    </div>
  );
}

function Thumb({ label, url, tone = "zinc", canOpen = false }) {
  const ring = { zinc: "border-zinc-200", emerald: "border-emerald-300", sky: "border-sky-300", lime: "border-lime-300" }[tone];
  const isImg = url && /\.(png|jpe?g|webp|gif)$/i.test(url);
  const inner = (
    <>
      <div className="aspect-[4/3] bg-zinc-50 flex items-center justify-center overflow-hidden">
        {url && isImg ? <img src={fileSrc(url)} alt={label} className="w-full h-full object-cover" />
          : url ? <FileText size={20} className="text-zinc-400" />
          : <span className="text-[10px] text-zinc-300">none</span>}
      </div>
      <div className="text-[10px] font-medium text-zinc-500 px-2 py-1 truncate">{label}</div>
    </>
  );
  const testid = `floorplan-thumb-${label.toLowerCase().replace(/[^a-z]/g, "-")}`;
  // Only CSM / management / admin may open + download. Everyone else sees a
  // non-clickable preview (no link to a full-size / downloadable image).
  if (canOpen && url) {
    return (
      <a href={fileSrc(url)} target="_blank" rel="noreferrer"
        className={`block rounded-lg border ${ring} overflow-hidden hover:opacity-90`}
        data-testid={testid}>
        {inner}
      </a>
    );
  }
  return (
    <div className={`block rounded-lg border ${ring} overflow-hidden select-none ${url ? "" : "opacity-60"}`}
      onContextMenu={(e) => e.preventDefault()}
      data-testid={testid}>
      {inner}
    </div>
  );
}

function WallDetail({ wall, pin, canCreate, moving, onMove, onClose, onDelete, onRecolor }) {
  const pk = pinStyle({ ...pin, kind: wall?.wall?.kind || pin.kind });
  const sm = wall?.site_measurement;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="floorplan-wall-detail">
      <div className="px-5 py-4 bg-zinc-900 text-white">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${pk.dot}`} /> {pk.label} · {wall?.wall?.scope || ""}
            </div>
            <div className="font-semibold truncate">{[wall?.wall?.group, wall?.wall?.label].filter(Boolean).join(" · ") || pin.label}</div>
            <div className="text-xs text-white/60 truncate">{wall?.wall?.scope}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white shrink-0" data-testid="floorplan-detail-close"><X size={18} /></button>
        </div>
      </div>

      {!wall ? (
        <div className="p-6 text-center text-sm text-zinc-400">Loading…</div>
      ) : (
        <div className="p-5 space-y-5">
          {/* thumbnails: site photo, approved design, approved pre-press, installed */}
          <div className={`grid gap-2 ${wall.designs_restricted ? "grid-cols-2" : "grid-cols-3"}`}>
            <Thumb label="Site photo" url={wall.site_photo} tone="sky" canOpen={canCreate} />
            {!wall.designs_restricted && <Thumb label="Approved design" url={wall.approved_design_url} tone="emerald" canOpen={canCreate} />}
            {!wall.designs_restricted && <Thumb label="Approved pre-press" url={wall.approved_prepress_url} tone="emerald" canOpen={canCreate} />}
            <Thumb label={`Installed photo${wall.install_photos_count > 1 ? ` · ${wall.install_photos_count}` : ""}`} url={wall.install_photo} tone="lime" canOpen={canCreate} />
          </div>

          {/* site measurement */}
          <div className="rounded-xl border border-zinc-200 p-3">
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 uppercase tracking-wider mb-1"><Ruler size={12} /> Site measurement</div>
            {sm?.measured || sm?.area_sqft ? (
              <div className="text-sm text-zinc-800" data-testid="floorplan-measurement">
                {sm.height && sm.width ? <span>{sm.height} × {sm.width} {sm.unit} · </span> : null}
                <b>{sm.area_sqft ? `${Number(sm.area_sqft).toLocaleString("en-IN")} sqft` : "—"}</b>
                {wall.site_photos_count > 1 && <span className="text-zinc-400 text-xs"> · {wall.site_photos_count} photos</span>}
              </div>
            ) : <div className="text-sm text-zinc-400">Not measured yet</div>}
          </div>

          {/* progress snapshot */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Progress</div>
            <div className="space-y-1.5">
              {wall.progress.map((s) => (
                <div key={s.stage} className="flex items-center gap-2 text-sm" data-testid={`floorplan-progress-${s.stage.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  {s.done ? <CheckCircle2 size={15} className="text-emerald-500 shrink-0" /> : <Circle size={15} className="text-zinc-300 shrink-0" />}
                  <span className="text-zinc-500 w-28 shrink-0">{s.stage}</span>
                  <span className={`truncate ${s.done ? "text-zinc-800 font-medium" : "text-zinc-400"}`}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* designer + pre-audit */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-zinc-200 p-3">
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 uppercase tracking-wider"><User size={12} /> Designer</div>
              <div className="text-sm font-medium text-zinc-800 mt-1 truncate">{wall.wall.designer_name || "—"}</div>
            </div>
            <div className="rounded-xl border border-zinc-200 p-3">
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 uppercase tracking-wider"><ShieldCheck size={12} /> Pre-audit</div>
              <div className="text-sm font-medium text-zinc-800 mt-1 capitalize">{wall.preaudit.status ? wall.preaudit.status.replace(/_/g, " ") : "—"}</div>
            </div>
          </div>

          {/* designs list */}
          {!wall.designs_restricted && (
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Designs shared ({wall.designs.length})</div>
            {wall.designs.length === 0 ? (
              <p className="text-xs text-zinc-400">No designs shared yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {wall.designs.map((d) => {
                  const cls = `flex items-center gap-2.5 px-2.5 py-2 rounded-lg border group ${d.is_approved ? "border-emerald-200 bg-emerald-50/60" : "border-zinc-200"} ${canCreate ? "hover:bg-zinc-50" : "select-none"}`;
                  const body = (
                    <>
                      <span className="w-7 h-7 rounded bg-zinc-100 flex items-center justify-center shrink-0"><ImageIcon size={14} className="text-zinc-400" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-zinc-800 truncate">{d.name}{d.version ? ` · v${d.version}` : ""}</span>
                        <span className="block text-[11px] text-zinc-400 truncate">{d.designer || "—"}{d.for_client ? " · client" : ""}</span>
                      </span>
                      {d.is_approved && <span className="text-[9px] font-mono uppercase bg-emerald-600 text-white px-1.5 py-0.5 rounded shrink-0">approved</span>}
                    </>
                  );
                  // Only CSM / management / admin may open + download designs.
                  return canCreate ? (
                    <a key={d.id} href={fileSrc(d.url)} target="_blank" rel="noreferrer"
                      data-testid={`floorplan-design-${d.id}`} className={cls}>{body}</a>
                  ) : (
                    <div key={d.id} data-testid={`floorplan-design-${d.id}`} className={cls}
                      onContextMenu={(e) => e.preventDefault()}>{body}</div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {canCreate && (
            <div className="flex items-center gap-4 pt-1">
              <button onClick={onMove} data-testid="floorplan-pin-move"
                className={`inline-flex items-center gap-1.5 text-xs font-medium ${moving ? "text-sky-600" : "text-zinc-600 hover:text-zinc-900"}`}>
                <Move size={13} /> {moving ? "Click the plan to drop it" : "Move pin"}
              </button>
              <button onClick={onDelete} data-testid="floorplan-pin-delete"
                className="inline-flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700 font-medium">
                <Trash2 size={13} /> Remove pin
              </button>
            </div>
          )}
          {canCreate && (wall?.wall?.kind || pin.kind) === "others" && (
            <div className="pt-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1.5">Pin colour</div>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="floorplan-detail-colors">
                {OTHER_COLORS.map((c) => (
                  <button key={c.v} onClick={() => onRecolor(c.v)} title={c.label}
                    data-testid={`floorplan-recolor-${c.v}`}
                    className={`w-6 h-6 rounded-full ${c.dot} ${(pin.color || "violet") === c.v ? "ring-2 ring-offset-1 ring-zinc-900" : "opacity-60 hover:opacity-100"}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
